import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from 'antd';
// pdf.js for OCR rasterization
// @ts-ignore - build export
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
// @ts-ignore - types provided via ambient declaration or installed package
import * as Tesseract from 'tesseract.js';
import { Icon, IconType } from 'modules/ui';

type PDFViewerProps = {
    pdfUrl: string;
    filename: string;
    hideControls?: boolean;
    documentTypeOptions?: { label: string; value: string }[];
    onClassify?: (documentType: string) => void;
    classifyLoading?: boolean;
    onDelete?: () => void;
    deleteLoading?: boolean;
    onSplit?: () => void;
};

// Configure pdf.js worker for OCR
// @ts-ignore
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.worker.min.js`;

const PDFViewer = ({ pdfUrl, filename, hideControls, documentTypeOptions, onClassify, classifyLoading, onDelete, deleteLoading, onSplit }: PDFViewerProps) => {
    const iframeRef = useRef(null);
    const intl = useIntl();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [numPages, setNumPages] = useState<number>(0);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [isOcrRunning, setIsOcrRunning] = useState<boolean>(false);
    const [ocrText, setOcrText] = useState<string>('');
    const [loadError, setLoadError] = useState<string>('');
    const [ocrError, setOcrError] = useState<string>('');
    const [isOcrVisible, setIsOcrVisible] = useState<boolean>(false);
    const [selectedClassifyType, setSelectedClassifyType] = useState<string>('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

    // If pdfUrl is a blob: revoke it on unmount
    const isBlobUrl = typeof pdfUrl === 'string' && pdfUrl.startsWith('blob:');

    // navpanes=0 and zoom are standard PDF open-parameters Chrome/Edge's built-in
    // viewer honors — collapses the thumbnail/bookmarks side panel and opens at a
    // more readable zoom level than the default "fit page" view.
    const viewerSrc = useMemo(() => {
        if (!pdfUrl) return pdfUrl;
        const separator = pdfUrl.includes('#') ? '&' : '#';
        return `${pdfUrl}${separator}navpanes=0&zoom=150`;
    }, [pdfUrl]);

    useEffect(() => {
        const iframe = iframeRef.current;

        return () => {
            try {
                if (iframe) {
                    // Stop loading & detach PDF plugin cleanly
                    iframe.src = 'about:blank';
                    // Some browsers unload only after src is cleared
                    iframe.removeAttribute('src');

                    // Nuke the document only when same-origin (accessing contentWindow.document
                    // on a cross-origin iframe throws SecurityError)
                    const win = iframe.contentWindow;
                    if (win) {
                        try {
                            win.document.open();
                            win.document.write('');
                            win.document.close();
                        } catch {
                            // Cross-origin frame: cannot access document. about:blank is enough.
                        }
                    }

                    // Remove from DOM
                    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                }
            } finally {
                // Revoke blob URL to avoid leaks
                if (isBlobUrl) {
                    try {
                        URL.revokeObjectURL(pdfUrl);
                    } catch {}
                }
            }
        };
    }, [pdfUrl, isBlobUrl]);

    useEffect(() => {
        return () => {
            if (isBlobUrl) {
                try {
                    URL.revokeObjectURL(pdfUrl);
                } catch {}
            }
        };
    }, [pdfUrl, isBlobUrl]);

    // Track container width to size pages responsively
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.clientWidth);
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);


    // Keep current page within available range when numPages changes
    useEffect(() => {
        if (!numPages) return;
    }, [numPages]);

    // Reset relevant state when url changes
    useEffect(() => {
        setNumPages(0);
        setLoadError('');
        setOcrText('');
        setIsOcrVisible(false);
    }, [pdfUrl]);

    const handleCopyOcrText = useCallback(async () => {
        if (!ocrText) return;
        try {
            await navigator.clipboard.writeText(ocrText);
        } catch {}
    }, [ocrText]);

    const runOcr = useCallback(async () => {
        if (!pdfUrl || isOcrRunning) return;
        setIsOcrRunning(true);
        setOcrText('');
        setOcrError('');
        try {
            let pdf: any | null = null;

            // Prefer fetching bytes ourselves to avoid CORS range/stream issues
            try {
                const resp = await fetch(pdfUrl, {
                    method: 'GET',
                    credentials: 'omit',
                    mode: 'cors',
                    cache: 'no-store',
                });
                if (!resp.ok) throw new Error('fetch-failed');
                const buffer = await resp.arrayBuffer();
                const byData = pdfjsLib.getDocument({
                    data: buffer,
                    useWorkerFetch: false,
                    disableStream: true,
                    disableRange: true,
                });
                pdf = await byData.promise;
            } catch (_) {
                // Fallback to direct URL if CORS allows it
                const byUrl = pdfjsLib.getDocument({
                    url: pdfUrl as string,
                    useWorkerFetch: false,
                    disableStream: true,
                    disableRange: true,
                    withCredentials: false,
                });
                pdf = await byUrl.promise;
            }
            const texts: string[] = [];

            for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
                const page = await pdf.getPage(pageNumber);
                // Render at 2x for better OCR accuracy
                const viewport = page.getViewport({ scale: 2 });
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                if (!context) continue;
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                await page.render({ canvasContext: context, viewport }).promise;

                // OCR this page
                const result = await Tesseract.recognize(canvas, 'eng');
                texts.push(result.data.text || '');

                // Cleanup canvas to free memory between pages
                canvas.width = 0;
                canvas.height = 0;
            }

            setOcrText(texts.join('\n\n').trim());
        } catch (err) {
            setOcrText('');
            setOcrError('Failed to extract text. Ensure bucket CORS allows your origin or proxy the file.');
        } finally {
            setIsOcrRunning(false);
        }
    }, [pdfUrl, isOcrRunning]);

    return (
        <div style={{ height: hideControls ? '85vh' : '90vh', border: '1px solid #d9d9d9', borderRadius: 6, backgroundColor: '#fafafa' }}>
            {!hideControls && (
                <div
                    style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #d9d9d9',
                        backgroundColor: '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, color: '#262626' }}>
                            {intl.formatMessage({ id: 'document.processing.type.pdf' })}
                        </span>
                        <span style={{ fontSize: 12, color: '#8c8c8c' }}>• {filename}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button
                            type="button"
                            onClick={runOcr}
                            disabled={isOcrRunning}
                            style={{
                                fontSize: 12,
                                color: isOcrRunning ? '#8c8c8c' : '#262626',
                                background: '#fff',
                                border: '1px solid #d9d9d9',
                                borderRadius: 4,
                                padding: '6px 10px',
                                cursor: isOcrRunning ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {isOcrRunning
                                ? intl.formatMessage({ id: 'extracting.text' })
                                : intl.formatMessage({ id: 'extract.text' })}
                        </button>
                        {ocrText && (
                            <button
                                type="button"
                                onClick={handleCopyOcrText}
                                style={{
                                    fontSize: 12,
                                    color: '#262626',
                                    background: '#fff',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: 4,
                                    padding: '6px 10px',
                                    cursor: 'pointer',
                                }}
                            >
                                {intl.formatMessage({ id: 'copy.extracted.text' })}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setIsOcrVisible(v => !v)}
                            disabled={!ocrText}
                            style={{
                                fontSize: 12,
                                color: !ocrText ? '#8c8c8c' : '#262626',
                                background: '#fff',
                                border: '1px solid #d9d9d9',
                                borderRadius: 4,
                                padding: '6px 10px',
                                cursor: !ocrText ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {isOcrVisible
                                ? intl.formatMessage({ id: 'hide.extracted.text' })
                                : intl.formatMessage({ id: 'show.extracted.text' })}
                        </button>
                        {/* Navigation and download are provided by the embedded viewer toolbar */}
                    </div>
                </div>
            )}
            {hideControls && documentTypeOptions && onClassify && (
                <div
                    style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid #d9d9d9',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                    }}
                >
                    <span style={{ fontSize: 14, color: '#262626' }}>
                        {intl.formatMessage({ id: 'document.processing.table.type', defaultMessage: 'Type' })}:
                    </span>
                    <select
                        value={selectedClassifyType}
                        onChange={(e) => setSelectedClassifyType(e.target.value)}
                        style={{
                            fontSize: 14,
                            padding: '6px 10px',
                            border: '1px solid #d9d9d9',
                            borderRadius: 4,
                            minWidth: 200,
                        }}
                    >
                        <option value="" disabled>
                            {intl.formatMessage({ id: 'document.processing.select.type', defaultMessage: 'Select document type' })}
                        </option>
                        {documentTypeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        disabled={!selectedClassifyType || classifyLoading}
                        onClick={() => onClassify(selectedClassifyType)}
                        style={{
                            fontSize: 14,
                            padding: '6px 16px',
                            border: 'none',
                            borderRadius: 4,
                            backgroundColor: selectedClassifyType && !classifyLoading ? '#ff5b5c' : '#d9d9d9',
                            color: '#fff',
                            cursor: selectedClassifyType && !classifyLoading ? 'pointer' : 'not-allowed',
                        }}
                    >
                        {classifyLoading
                            ? intl.formatMessage({ id: 'document.processing.action.submitting', defaultMessage: 'Submitting...' })
                            : intl.formatMessage({ id: 'document.processing.action.submit', defaultMessage: 'Submit' })}
                    </button>
                    {onSplit && (
                        <button
                            type="button"
                            disabled={classifyLoading}
                            onClick={onSplit}
                            style={{
                                fontSize: 14,
                                padding: '6px 16px',
                                border: '1px solid #722ed1',
                                borderRadius: 4,
                                backgroundColor: '#fff',
                                color: '#722ed1',
                                cursor: classifyLoading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {intl.formatMessage({ id: 'document.processing.action.split', defaultMessage: 'Split' })}
                        </button>
                    )}
                    {onDelete && (
                        <button
                            type="button"
                            disabled={deleteLoading || classifyLoading}
                            onClick={() => setShowDeleteConfirm(true)}
                            style={{
                                fontSize: 14,
                                padding: '6px 16px',
                                border: '1px solid #d9d9d9',
                                borderRadius: 4,
                                backgroundColor: '#fff',
                                color: '#cf1322',
                                cursor: deleteLoading || classifyLoading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {deleteLoading
                                ? intl.formatMessage({ id: 'document.processing.action.deleting', defaultMessage: 'Deleting...' })
                                : intl.formatMessage({ id: 'document.processing.action.delete.document', defaultMessage: 'Delete' })}
                        </button>
                    )}
                </div>
            )}
            {onDelete && (
                <Modal
                    title={intl.formatMessage({ id: 'document.processing.action.delete.document' })}
                    visible={showDeleteConfirm}
                    onOk={() => {
                        onDelete();
                        setShowDeleteConfirm(false);
                    }}
                    onCancel={() => setShowDeleteConfirm(false)}
                    okText={intl.formatMessage({ id: 'document.processing.action.delete.document', defaultMessage: 'Delete' })}
                    cancelText={intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
                >
                    <p>{intl.formatMessage({ id: 'document.processing.delete.confirmation' })}</p>
                </Modal>
            )}

            <div
                ref={containerRef}
                style={{
                    height: 'calc(100% - 60px)',
                    padding: 16,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <div style={{ height: '100%', borderRadius: 4, overflow: 'hidden' }}>
                    <iframe
                        ref={iframeRef}
                        src={viewerSrc}
                        title={filename}
                        style={{ width: '100%', height: '100%', border: 'none', borderRadius: 4 }}
                    />
                </div>

                {loadError && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 24,
                            right: 24,
                            maxWidth: '40%',
                            border: '1px solid #ffa39e',
                            background: '#fff1f0',
                            color: '#cf1322',
                            borderRadius: 6,
                            padding: 12,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                    >
                        <div style={{ fontSize: 12 }}>{loadError}</div>
                    </div>
                )}

                {isOcrVisible && ocrText && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 24,
                            right: 24,
                            width: '40%',
                            maxHeight: '40%',
                            overflow: 'auto',
                            border: '1px solid #d9d9d9',
                            borderRadius: 6,
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 12,
                                borderBottom: '1px solid #f0f0f0',
                            }}
                        >
                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                {intl.formatMessage({ id: 'extracted.text' })}
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsOcrVisible(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#8c8c8c',
                                }}
                                aria-label={intl.formatMessage({ id: 'hide.extracted.text' })}
                            >
                                <Icon type={IconType.CLOSE} style={{ fontSize: 16 }} />
                            </button>
                        </div>
                        <div style={{ padding: 12, overflow: 'auto' }}>
                            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, margin: 0 }}>{ocrText}</pre>
                        </div>
                    </div>
                )}

                {ocrError && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 24,
                            left: 24,
                            maxWidth: '40%',
                            border: '1px solid #ffa39e',
                            background: '#fff1f0',
                            color: '#cf1322',
                            borderRadius: 6,
                            padding: 12,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                    >
                        <div style={{ fontSize: 12 }}>{ocrError}</div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PDFViewer;
